import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { getAbiItem, getAddress, isAddress, keccak256, toHex, type Address, type Hex } from 'viem';
import { useConnection, usePublicClient, useReadContract, useReadContracts } from 'wagmi';
import {
  bondDepositoryAbi,
  buybackExecutorAbi,
  syntraTimelockAbi,
  minterRegistryAbi,
  priceOracleAbi,
  referralManagerAbi,
  stakingManagerAbi,
} from '@syntra/abi';
import { ActionButton } from '../components/tx/ActionButton.tsx';
import { BadgeTitle, Button, Card, Notice, Pill, Rows, SectionHead, Segmented } from '../components/ui/primitives.tsx';
import { CHAIN_ID, DEPLOYMENT as D, EXPLORER } from '../config/env.ts';
import { useChainNow } from '../hooks/useChainClock.tsx';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useTimelockOps } from '../hooks/useTimelockOps.ts';
import { describeCall } from '../lib/timelock.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import {
  buildCalls,
  CANCELLER_ROLE,
  PROPOSER_ROLE,
  UUPS_UUID,
  safeBuilderJson,
  scheduleCallsCalldata,
  scheduleRequest,
  type Call,
  type Change,
  type MarketConfig,
  type RoleChange,
} from '../lib/admin.ts';
import { fmtBps, fmtDuration, fmtPct, fmtToken, shortAddr, toNum } from '../lib/format.ts';
import { pagedLogs } from '../lib/logs.ts';
import { MARKETS } from './Bond.tsx';
import { LONGEST_LOCK, PERMANENT, PLAN_IDS } from '../hooks/useProtocol.ts';

type Kind = Change['kind'] | RoleChange['kind'];

function useAdminData() {
  const q = useReadContracts({
    allowFailure: true,
    query: { refetchInterval: 20_000 },
    contracts: [
      { address: D.staking, abi: stakingManagerAbi, functionName: 'rates' },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'fees' },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'maxRate' },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'feeRecipient' },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [1] },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [LONGEST_LOCK] },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'paused' },
      { address: D.bonds, abi: bondDepositoryAbi, functionName: 'paused' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'paused' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'directBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'minSideBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'capMultiplierBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'maxDepth' },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'minterConfig', args: [D.staking] },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'minterConfig', args: [D.bonds] },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'minterConfig', args: [D.referral] },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'available', args: [D.staking] },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'available', args: [D.bonds] },
      { address: D.registry, abi: minterRegistryAbi, functionName: 'available', args: [D.referral] },
      { address: D.buyback, abi: buybackExecutorAbi, functionName: 'config' },
      { address: D.buyback, abi: buybackExecutorAbi, functionName: 'remainingToday' },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'twapPeriod' },
      { address: D.oracle, abi: priceOracleAbi, functionName: 'maxSpotDeviationBps' },
      { address: D.timelock, abi: syntraTimelockAbi, functionName: 'getMinDelay' },
    ],
  });
  const mq = useReadContracts({
    allowFailure: true,
    query: { refetchInterval: 20_000 },
    contracts: MARKETS.map((m) => ({ address: D.bonds, abi: bondDepositoryAbi, functionName: 'market', args: [BigInt(m.id)] }) as const),
  });
  const r = q.data;
  const v = <T,>(i: number) => (r?.[i]?.status === 'success' ? (r[i].result as T) : undefined);
  return {
    rates: v<readonly bigint[]>(0),
    fees: v<readonly [readonly bigint[], readonly bigint[]]>(1),
    maxRate: v<bigint>(2),
    feeRecipient: v<Address>(3),
    lock30: v<{ lockDuration: bigint }>(4)?.lockDuration,
    lock365: v<{ lockDuration: bigint }>(5)?.lockDuration,
    paused: { staking: v<boolean>(6), bonds: v<boolean>(7), referral: v<boolean>(8) },
    directBps: v<number>(9),
    minSideBps: v<number>(10),
    capBps: v<number>(11),
    maxDepth: v<number>(12),
    caps: [v<{ capPerEpoch: bigint; enabled: boolean }>(13), v<{ capPerEpoch: bigint; enabled: boolean }>(14), v<{ capPerEpoch: bigint; enabled: boolean }>(15)],
    available: [v<bigint>(16), v<bigint>(17), v<bigint>(18)],
    buyback: v<{ triggerBps: number; slippageBps: number; cooldown: number; maxPerTrade: bigint; maxPerDay: bigint; burnBought: boolean }>(19),
    remainingToday: v<bigint>(20),
    twapPeriod: v<number>(21),
    maxDev: v<number>(22),
    delay: v<bigint>(23),
    markets: MARKETS.map((_, i) => (mq.data?.[i]?.status === 'success' ? (mq.data[i].result as readonly [MarketConfig, unknown])[0] : undefined)),
  };
}

/** The admin wallets: every account holding the timelock's proposer role, replayed from its role events. */
function useAdminWallets() {
  return useRoleHolders(PROPOSER_ROLE);
}

/** The veto wallets: cancellers that are not admins. They can stop a scheduled change and nothing else (H-2). */
function useVetoWallets() {
  const cancellers = useRoleHolders(CANCELLER_ROLE);
  const admins = useAdminWallets();
  const adminSet = new Set((admins.data ?? []).map((a) => a.toLowerCase()));
  return { data: cancellers.data?.filter((c) => !adminSet.has(c.toLowerCase())), isPending: cancellers.isPending || admins.isPending };
}

function useRoleHolders(role: Hex) {
  const client = usePublicClient();
  return useQuery({
    queryKey: ['role-holders', role],
    refetchInterval: 30_000,
    enabled: Boolean(client),
    queryFn: async (): Promise<Address[]> => {
      const from = BigInt(D.deployedAtBlock ?? 0);
      const head = await client!.getBlockNumber();
      const args = { role };
      const [granted, revoked] = await Promise.all([
        pagedLogs(head, from, (fromBlock, toBlock) =>
          client!.getLogs({ address: D.timelock, event: getAbiItem({ abi: syntraTimelockAbi, name: 'RoleGranted' }), args, fromBlock, toBlock }),
        ),
        pagedLogs(head, from, (fromBlock, toBlock) =>
          client!.getLogs({ address: D.timelock, event: getAbiItem({ abi: syntraTimelockAbi, name: 'RoleRevoked' }), args, fromBlock, toBlock }),
        ),
      ]);
      const order = (l: { blockNumber: bigint | null; logIndex: number | null }) => Number(l.blockNumber ?? 0n) * 1e5 + (l.logIndex ?? 0);
      const events = [...granted.map((l) => ({ l, on: true })), ...revoked.map((l) => ({ l, on: false }))].sort((x, y) => order(x.l) - order(y.l));
      const held = new Map<string, Address>();
      for (const { l, on } of events) {
        const account = l.args.account!;
        if (on) held.set(account.toLowerCase(), account);
        else held.delete(account.toLowerCase());
      }
      return [...held.values()];
    },
  });
}

export function Admin() {
  const { t } = useI18n();
  const { address } = useConnection();
  const a = useAdminData();
  const admins = useAdminWallets().data;
  const veto = useVetoWallets();
  const { proposer: isProposer, pauser: isPauser, canceller: isCanceller } = useIsAdmin();
  const delayText = a.delay !== undefined ? fmtDuration(Number(a.delay)) : '—';
  const pill = (p: boolean | undefined) => (p ? <Pill tone="red">{t('admin.paused')}</Pill> : <Pill tone="green">{t('admin.active')}</Pill>);
  const wallets = (list: Address[]) =>
    list.map((w, i) => (
      <span key={w} className="num" title={w}>
        {i > 0 ? ', ' : ''}
        {shortAddr(w)}
      </span>
    ));
  return (
    <>
      <SectionHead eyebrow={t('admin.eyebrow')} title={t('admin.title')} />
      <Notice tone={isProposer ? 'ok' : undefined}>
        {t('admin.roleNote', { delay: delayText })} {address ? (isProposer ? t('admin.youProposer') : isCanceller ? t('admin.youCanceller') : t('admin.notProposer')) : ''}
        {admins && admins.length > 0 ? (
          <span className="admin-wallets" data-testid="admin-wallets">
            {' '}
            {t('admin.wallets')} {wallets(admins)}
          </span>
        ) : null}
        {veto.data && veto.data.length > 0 ? (
          <span className="admin-wallets" data-testid="veto-wallets">
            {' '}
            {t('admin.vetoWallets')} {wallets(veto.data)}
          </span>
        ) : null}
      </Notice>
      {veto.data && veto.data.length === 0 ? (
        <div data-testid="no-veto">
          <Notice tone="warn">{t('admin.noVeto')}</Notice>
        </div>
      ) : null}

      <div className="grid two">
        <Card className="badge-card" title={<BadgeTitle icon="stake">{t('admin.staking')}</BadgeTitle>}>
          <Rows
            items={[
              ['APR (no lock · 30 · 90 · 180 · 365 d · permanent)', a.rates ? a.rates.map((r) => fmtPct(toNum(r) * 730, 0)).join(' · ') : '—'],
              ['Max rate', `${fmtPct(toNum(a.maxRate), 3)} per epoch`],
              ['Locks', a.lock30 !== undefined && a.lock365 !== undefined ? `${fmtDuration(Number(a.lock30))} … ${fmtDuration(Number(a.lock365))}, permanent` : '—'],
              [
                'Fees in / out',
                a.fees
                  ? `no lock ${fmtBps(a.fees[0][0])} / ${fmtBps(a.fees[1][0])} · locked ${fmtBps(a.fees[0][1])} / ${fmtBps(a.fees[1][1])} · permanent ${fmtBps(a.fees[0][PERMANENT])} / ${fmtBps(a.fees[1][PERMANENT])}`
                  : '—',
              ],
              ['', pill(a.paused.staking)],
            ]}
          />
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="bond">{t('admin.bonds')}</BadgeTitle>}>
          <Rows
            items={[
              ...MARKETS.map((m, i): [string, string] => {
                const c = a.markets[i];
                return [m.symbol, c ? `${c.enabled ? 'open' : 'closed'} · ROI ${fmtBps(c.roiMinBps)}–${fmtBps(c.roiMaxBps)} · cap ${fmtToken(c.capacityPerPeriod, 0)}` : '—'];
              }),
              ['', pill(a.paused.bonds)],
            ]}
          />
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="referral">{t('admin.referral')}</BadgeTitle>}>
          <Rows
            items={[
              ['Direct', a.directBps !== undefined ? fmtBps(a.directBps) : '—'],
              ['Min per side', a.minSideBps !== undefined ? fmtBps(a.minSideBps) : '—'],
              ['Cap', a.capBps !== undefined ? fmtBps(a.capBps) : '—'],
              ['Max depth', String(a.maxDepth ?? '—')],
              ['', pill(a.paused.referral)],
            ]}
          />
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="sparkle">{t('admin.mint')}</BadgeTitle>}>
          <Rows
            items={(['Staking', 'Bonds', 'Referral'] as const).map((name, i): [string, string] => [
              name,
              a.caps[i] ? `${fmtToken(a.caps[i]!.capPerEpoch, 0)} · ${fmtToken(a.available[i], 0)} ${t('admin.available')}` : '—',
            ])}
          />
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="swap">{t('admin.buyback')}</BadgeTitle>}>
          <Rows
            items={
              a.buyback
                ? [
                    ['Trigger', fmtBps(a.buyback.triggerBps)],
                    ['Per trade / day', `${fmtToken(a.buyback.maxPerTrade, 0)} / ${fmtToken(a.buyback.maxPerDay, 0)} USDT`],
                    ['Left today', `${fmtToken(a.remainingToday, 0)} USDT`],
                    ['Slippage · cooldown', `${fmtBps(a.buyback.slippageBps)} · ${fmtDuration(a.buyback.cooldown)}`],
                    ['Bought SYN', a.buyback.burnBought ? 'burned' : 'to treasury'],
                  ]
                : []
            }
          />
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="info">{t('admin.oracle')}</BadgeTitle>}>
          <Rows
            items={[
              ['TWAP period', a.twapPeriod !== undefined ? fmtDuration(a.twapPeriod) : '—'],
              ['Max spot deviation', a.maxDev !== undefined ? fmtBps(a.maxDev) : '—'],
              ['Timelock delay', delayText],
            ]}
          />
        </Card>
      </div>

      <div className="grid two">
        <ProposeCard data={a} canSchedule={Boolean(isProposer)} admins={admins} vetoes={veto.data} />
        <div className="grid">
          <QueueCard />
          <Card title={t('admin.emergency')}>
            <p className="soft" style={{ marginTop: 0 }}>
              {t('admin.emergencyNote')}
            </p>
            {!address ? (
              <p className="soft">{t('common.connectFirst')}</p>
            ) : !isPauser ? (
              <Notice>{t('admin.pauseSafeOnly')}</Notice>
            ) : null}
            {/* one switch per contract: each pauses on its own, so the Safe can stop just the part that needs it */}
            <ul className="pause-list">
              {(
                [
                  ['staking', D.staking, stakingManagerAbi],
                  ['bonds', D.bonds, bondDepositoryAbi],
                  ['referral', D.referral, referralManagerAbi],
                ] as const
              ).map(([name, target, abi]) => (
                <li key={name} className="pause-row" data-testid={`pause-${name}`}>
                  <b>{t(`admin.${name}`)}</b>
                  {pill(a.paused[name])}
                  {isPauser ? (
                    <div className="pause-action">
                      <ActionButton
                        variant="secondary"
                        label={t('admin.pause', { module: t(`admin.${name}`) })}
                        blocked={a.paused[name] ? t('admin.paused') : null}
                        request={{ address: target, abi: abi as typeof stakingManagerAbi, functionName: 'pause' }}
                      />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

function ProposeCard({
  data: a,
  canSchedule,
  admins,
  vetoes,
}: {
  data: ReturnType<typeof useAdminData>;
  canSchedule: boolean;
  admins: Address[] | undefined;
  vetoes: Address[] | undefined;
}) {
  const { t } = useI18n();
  const [kind, setKind] = useState<Kind>('rates');
  const [f, setF] = useState<Record<string, string>>({});
  const [marketIdx, setMarketIdx] = useState(1);
  const [flag, setFlag] = useState(true);
  const [copied, setCopied] = useState(false);
  const salt = useMemo(() => keccak256(toHex(`${kind}-${Date.now()}`)), [kind, f, marketIdx, flag]); // eslint-disable-line react-hooks/exhaustive-deps
  const get = (k: string, fallback: string) => f[k] ?? fallback;
  const set = (k: string) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  const market = a.markets[marketIdx];
  let change: Change | RoleChange | null = null;
  if (kind === 'rates')
    change = { kind, aprPct: PLAN_IDS.map((i) => get(`apr${i}`, a.rates ? String(Math.round(toNum(a.rates[i]) * 730 * 10_000) / 100) : '0')) };
  if (kind === 'fees' && a.fees && a.feeRecipient)
    change = {
      kind,
      noLockInPct: get('noLockIn', String(Number(a.fees[0][0]) / 100)),
      noLockOutPct: get('noLockOut', String(Number(a.fees[1][0]) / 100)),
      lockedInPct: get('lockedIn', String(Number(a.fees[0][1]) / 100)),
      lockedOutPct: get('lockedOut', String(Number(a.fees[1][1]) / 100)),
      permanentInPct: get('permanentIn', String(Number(a.fees[0][PERMANENT]) / 100)),
      permanentOutPct: get('permanentOut', String(Number(a.fees[1][PERMANENT]) / 100)),
      recipient: a.feeRecipient,
    };
  if (kind === 'market' && market)
    change = {
      kind,
      marketId: MARKETS[marketIdx].id,
      current: market,
      enabled: flag,
      roiMinPct: get('roiMin', String(market.roiMinBps / 100)),
      roiMaxPct: get('roiMax', String(market.roiMaxBps / 100)),
      capacity: get('capacity', String(toNum(market.capacityPerPeriod))),
    };
  if (kind === 'buyback' && a.buyback)
    change = {
      kind,
      triggerPct: get('trigger', String(a.buyback.triggerBps / 100)),
      maxPerTrade: get('maxTrade', String(toNum(a.buyback.maxPerTrade))),
      maxPerDay: get('maxDay', String(toNum(a.buyback.maxPerDay))),
      slippagePct: get('slip', String(a.buyback.slippageBps / 100)),
      cooldownHours: get('cooldown', String(a.buyback.cooldown / 3600)),
      burn: flag,
    };
  if (kind === 'unpause') change = { kind, module: (f.module as 'staking' | 'bonds' | 'referral') ?? 'staking' };
  const account = (f.account ?? '').trim();
  const isAdmin = admins?.some((w) => w.toLowerCase() === account.toLowerCase()) ?? false;
  const isVeto = vetoes?.some((w) => w.toLowerCase() === account.toLowerCase()) ?? false;
  let roleBlock: string | null = null;
  if (kind === 'addAdmin' || kind === 'removeAdmin') {
    if (!isAddress(account)) roleBlock = t('admin.walletInvalid');
    else if (kind === 'addAdmin' && isAdmin) roleBlock = t('admin.walletAlready');
    else if (kind === 'removeAdmin' && !isAdmin) roleBlock = t('admin.walletNotAdmin');
    else if (kind === 'removeAdmin' && (admins?.length ?? 0) <= 1) roleBlock = t('admin.walletLast'); // no admin left = the timelock is frozen for good
    else change = { kind, account: getAddress(account) };
  }
  if (kind === 'addVeto' || kind === 'removeVeto') {
    if (!isAddress(account)) roleBlock = t('admin.walletInvalid');
    else if (kind === 'addVeto' && (isVeto || isAdmin)) roleBlock = t('admin.vetoAlready'); // admins already hold the canceller role
    else if (kind === 'removeVeto' && !isVeto) roleBlock = t('admin.vetoNotVeto');
    else change = { kind, account: getAddress(account) };
  }
  // an upgrade: the proxy to move and the new implementation, which must answer the UUPS check before anything is scheduled
  const impl = (f.impl ?? '').trim();
  const proxy = (f.proxy as 'staking' | 'bonds' | 'referral' | undefined) ?? 'staking';
  const uuid = useReadContract({
    address: isAddress(impl) ? getAddress(impl) : undefined,
    abi: stakingManagerAbi,
    functionName: 'proxiableUUID',
    query: { enabled: kind === 'upgrade' && isAddress(impl), retry: false },
  });
  let implBlock: string | null = null;
  if (kind === 'upgrade') {
    if (!isAddress(impl)) implBlock = t('admin.implInvalid');
    else if (uuid.isPending) implBlock = t('admin.implChecking');
    else if (uuid.data !== UUPS_UUID) implBlock = t('admin.implNotUups');
    else change = { kind, proxy, implementation: getAddress(impl) };
  }

  let built: Call[] | null = null;
  let buildError: string | null = null;
  try {
    built = change ? buildCalls(change, D) : null;
  } catch (e) {
    buildError = (e as Error).message.split('\n')[0];
  }
  const delay = a.delay ?? 0n;
  const calldata = built ? scheduleCallsCalldata(built, delay, salt) : null;
  const field = (k: string, label: string, value: string) => (
    <label className="field" key={k}>
      <span className="field-top">{label}</span>
      <input className="text-input num" value={value} onChange={set(k)} inputMode="decimal" aria-label={label} />
    </label>
  );
  return (
    <Card title={t('admin.propose')} highlight>
      <div className="field" style={{ gap: 12 }}>
        <label className="field">
          <span className="field-top">{t('admin.action')}</span>
          <select
            className="text-input"
            aria-label={t('admin.action')}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as Kind);
              setF({});
              setFlag(e.target.value === 'market' ? Boolean(a.markets[marketIdx]?.enabled) : e.target.value === 'buyback' ? Boolean(a.buyback?.burnBought) : true);
            }}
          >
            <option value="rates">{t('admin.actRates')}</option>
            <option value="fees">{t('admin.actFees')}</option>
            <option value="market">{t('admin.actMarket')}</option>
            <option value="buyback">{t('admin.actBuyback')}</option>
            <option value="unpause">{t('admin.actUnpause')}</option>
            <option value="addAdmin">{t('admin.actAddAdmin')}</option>
            <option value="removeAdmin">{t('admin.actRemoveAdmin')}</option>
            <option value="addVeto">{t('admin.actAddVeto')}</option>
            <option value="removeVeto">{t('admin.actRemoveVeto')}</option>
            <option value="upgrade">{t('admin.actUpgrade')}</option>
          </select>
        </label>
        {kind === 'rates' && change?.kind === 'rates' ? (
          <>
            {change.aprPct.map((v, i) => field(`apr${i}`, t('admin.planApr', { plan: t(`plan.${i}` as MessageKey) }), v))}
          </>
        ) : null}
        {kind === 'fees' && change?.kind === 'fees' ? (
          <>
            {field('noLockIn', t('admin.feeNoLockIn'), change.noLockInPct)}
            {field('noLockOut', t('admin.feeNoLockOut'), change.noLockOutPct)}
            {field('lockedIn', t('admin.feeLockedIn'), change.lockedInPct)}
            {field('lockedOut', t('admin.feeLockedOut'), change.lockedOutPct)}
            {field('permanentIn', t('admin.feePermanentIn'), change.permanentInPct)}
            {field('permanentOut', t('admin.feePermanentOut'), change.permanentOutPct)}
          </>
        ) : null}
        {kind === 'market' && change?.kind === 'market' ? (
          <>
            <Segmented<string>
              label={t('admin.market')}
              value={String(marketIdx)}
              onChange={(v) => {
                setMarketIdx(Number(v));
                setF({});
                setFlag(Boolean(a.markets[Number(v)]?.enabled));
              }}
              options={MARKETS.map((m, i) => ({ value: String(i), label: m.symbol }))}
            />
            <label className="row">
              <span>{t('admin.enabled')}</span>
              <input type="checkbox" checked={flag} onChange={(e) => setFlag(e.target.checked)} />
            </label>
            {field('roiMin', t('admin.roiMin'), change.roiMinPct)}
            {field('roiMax', t('admin.roiMax'), change.roiMaxPct)}
            {field('capacity', t('admin.capacity'), change.capacity)}
          </>
        ) : null}
        {kind === 'buyback' && change?.kind === 'buyback' ? (
          <>
            {field('trigger', t('admin.trigger'), change.triggerPct)}
            {field('maxTrade', t('admin.maxTrade'), change.maxPerTrade)}
            {field('maxDay', t('admin.maxDay'), change.maxPerDay)}
            {field('slip', t('admin.slip'), change.slippagePct)}
            {field('cooldown', t('admin.cooldownH'), change.cooldownHours)}
            <label className="row">
              <span>{t('admin.burn')}</span>
              <input type="checkbox" checked={flag} onChange={(e) => setFlag(e.target.checked)} />
            </label>
          </>
        ) : null}
        {kind === 'unpause' ? (
          <Segmented<string>
            label={t('admin.module')}
            value={f.module ?? 'staking'}
            onChange={(v) => setF({ module: v })}
            options={['staking', 'bonds', 'referral'].map((m) => ({ value: m, label: m }))}
          />
        ) : null}
        {kind === 'addAdmin' || kind === 'removeAdmin' || kind === 'addVeto' || kind === 'removeVeto' ? (
          <>
            <label className="field">
              <span className="field-top">{t('admin.wallet')}</span>
              <input className="text-input num" value={f.account ?? ''} onChange={set('account')} placeholder="0x…" spellCheck={false} autoComplete="off" aria-label={t('admin.wallet')} />
            </label>
            <p className="soft" style={{ margin: 0 }}>
              {t(kind === 'addAdmin' ? 'admin.addAdminNote' : kind === 'removeAdmin' ? 'admin.removeAdminNote' : kind === 'addVeto' ? 'admin.vetoNote' : 'admin.removeVetoNote')}
            </p>
            {roleBlock && account ? <Notice tone="warn">{roleBlock}</Notice> : null}
          </>
        ) : null}
        {kind === 'upgrade' ? (
          <>
            <Segmented<string>
              label={t('admin.proxy')}
              value={proxy}
              onChange={(v) => setF((x) => ({ ...x, proxy: v }))}
              options={['staking', 'bonds', 'referral'].map((m) => ({ value: m, label: t(`admin.${m}` as MessageKey) }))}
            />
            <label className="field">
              <span className="field-top">{t('admin.implementation')}</span>
              <input className="text-input num" value={impl} onChange={set('impl')} placeholder="0x…" spellCheck={false} autoComplete="off" aria-label={t('admin.implementation')} />
            </label>
            <p className="soft" style={{ margin: 0 }}>
              {t('admin.upgradeNote')}
            </p>
            {implBlock && impl ? <Notice tone="warn">{implBlock}</Notice> : null}
            {!implBlock && change?.kind === 'upgrade' ? (
              <Notice tone="ok">
                {t('admin.implOk')}{' '}
                {EXPLORER ? (
                  <a href={`${EXPLORER}/address/${change.implementation}#code`} target="_blank" rel="noreferrer">
                    {t('admin.implSource')}
                  </a>
                ) : null}
              </Notice>
            ) : null}
          </>
        ) : null}
        {buildError ? <Notice tone="error">{buildError}</Notice> : null}
        {built && calldata ? (
          <>
            <Rows items={[[t('admin.target'), <span key="t" className="num">{[...new Set(built.map((x) => x.target))].map(shortAddr).join(', ')}</span>]]} />
            <textarea
              className="text-input num"
              readOnly
              rows={3}
              value={built.map((x) => x.data).join('\n')}
              aria-label={t('admin.calldata')}
              style={{ minHeight: 72, padding: 10, fontSize: 12 }}
            />
            <ActionButton
              label={t('admin.schedule')}
              blocked={canSchedule ? null : t('admin.needAdmin')} // the notice at the top says why and which wallets are admins
              request={{ address: D.timelock, abi: syntraTimelockAbi, ...scheduleRequest(built, delay, salt) }}
            />
            <Button
              variant="ghost"
              onClick={async () => {
                await navigator.clipboard?.writeText(safeBuilderJson(CHAIN_ID, D.timelock, calldata, kind)).catch(() => undefined);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? t('admin.copied') : t('admin.copyJson')}
            </Button>
          </>
        ) : null}
      </div>
    </Card>
  );
}

/** Scheduled operations from the timelock's events, with their state read live, each call in words. */
function QueueCard() {
  const { t } = useI18n();
  const now = useChainNow(5000);
  const { canceller: isCanceller } = useIsAdmin();
  const { ops: list } = useTimelockOps();
  const label = ['—', t('admin.waiting'), t('admin.ready'), t('admin.done')];
  return (
    <Card title={t('admin.queue')}>
      {list.length === 0 ? (
        <p className="soft">{t('admin.noQueue')}</p>
      ) : (
        <table className="table stack" data-testid="queue">
          <thead>
            <tr>
              <th>{t('admin.op')}</th>
              <th>{t('admin.eta')}</th>
              <th>{t('admin.state')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((o) => {
              const state = o.state;
              const ts = o.timestamp;
              const ready = state === 2 || (state === 1 && ts > 1 && now >= ts);
              return (
                <tr key={o.id}>
                  <td data-label={t('admin.op')} title={`${o.id}\n${o.calls.map((c) => `${c.target} ${c.data}`).join('\n')}`}>
                    {describeCall(o.calls[0].target, o.calls[0].data, t)}
                    {o.calls.length > 1 ? ` + ${t('admin.moreCalls', { n: o.calls.length - 1 })}` : ''}
                  </td>
                  <td data-label={t('admin.eta')}>{ts > 1 ? new Date(ts * 1000).toLocaleString() : '—'}</td>
                  <td data-label={t('admin.state')}>
                    <Pill tone={state === 3 ? 'muted' : ready ? 'green' : state === 0 ? 'red' : 'amber'}>
                      {state === 0 ? t('admin.cancelled') : ready ? t('admin.ready') : label[state]}
                    </Pill>
                  </td>
                  <td className="queue-action">
                    {ready ? (
                      <ActionButton
                        variant="secondary"
                        label={t('admin.execute')}
                        request={
                          o.calls.length === 1
                            ? { address: D.timelock, abi: syntraTimelockAbi, functionName: 'execute', args: [o.calls[0].target, 0n, o.calls[0].data, o.predecessor, o.salt] }
                            : {
                                address: D.timelock,
                                abi: syntraTimelockAbi,
                                functionName: 'executeBatch',
                                args: [o.calls.map((c) => c.target), o.calls.map(() => 0n), o.calls.map((c) => c.data), o.predecessor, o.salt],
                              }
                        }
                      />
                    ) : null}
                    {isCanceller && (state === 1 || state === 2) ? (
                      <ActionButton variant="secondary" label={t('admin.cancel')} request={{ address: D.timelock, abi: syntraTimelockAbi, functionName: 'cancel', args: [o.id] }} />
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}
